import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Play, ChevronDown, Loader2, Settings2 } from 'lucide-react';

/**
 * SplitRunButton - Split button with main Run action and dropdown menu
 * 
 * Props:
 * - onRun: () => void - Main run action
 * - onAdvancedRun: () => void - Opens advanced run modal
 * - disabled: boolean - Disable all interactions
 * - isLoading: boolean - Show loading state
 */
const SplitRunButton = ({ 
  onRun, 
  onAdvancedRun, 
  disabled = false, 
  isLoading = false 
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ bottom: 0, left: 0, minWidth: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Position menu above the button
  useEffect(() => {
    if (showMenu && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        minWidth: rect.width
      });
    }
  }, [showMenu]);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleMainClick = () => {
    if (disabled || isLoading) return;
    onRun();
  };

  const handleMenuClick = () => {
    if (disabled || isLoading) return;
    setShowMenu(!showMenu);
  };

  const handleAdvancedRun = () => {
    setShowMenu(false);
    onAdvancedRun();
  };

  const isDisabled = disabled || isLoading;

  return (
    <div ref={triggerRef} className="flex">
      {/* Main Run Button */}
      <button
        onClick={handleMainClick}
        disabled={isDisabled}
        className={`
          flex items-center justify-center gap-2 px-4 py-2 rounded-l-lg font-medium text-sm
          ${!isDisabled
            ? 'bg-blue-600 hover:bg-blue-500 text-white' 
            : 'bg-gray-700 text-gray-500 cursor-not-allowed'
          }
          transition-colors
        `}
      >
        {isLoading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            <span>Launching</span>
          </>
        ) : (
          <>
            <Play size={16} />
            <span>Run</span>
          </>
        )}
      </button>

      {/* Dropdown Trigger */}
      <button
        onClick={handleMenuClick}
        disabled={isDisabled}
        className={`
          flex items-center justify-center px-2 py-2 rounded-r-lg border-l
          ${!isDisabled
            ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-500' 
            : 'bg-gray-700 text-gray-500 cursor-not-allowed border-gray-600'
          }
          transition-colors
        `}
      >
        <ChevronDown 
          size={16} 
          className={`transition-transform ${showMenu ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu (Portal) */}
      {showMenu && createPortal(
        <div
          ref={menuRef}
          className="fixed bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden"
          style={{
            zIndex: 9999,
            bottom: menuPosition.bottom,
            left: menuPosition.left,
            minWidth: menuPosition.minWidth
          }}
        >
          <button
            onClick={handleAdvancedRun}
            className="w-full px-4 py-3 flex items-center gap-3 text-left text-sm text-gray-200 hover:bg-gray-700 transition-colors"
          >
            <Settings2 size={16} className="text-gray-400" />
            <span>Run with flags/variables...</span>
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SplitRunButton;

